import axios from 'axios';
import moment from 'moment';

import appwriteService from '@/config/appwrite';
import { addEventToQueue } from '@/utils/Events/eventQueue';

// ─── Validate-Event API Types ─────────────────────────────────────────────────

/** Free-form payload — event, source, source_type, meta and any extra fields */
export interface ActivityEventPayload {
  event: string;
  source: string;
  source_type?: string;
  meta?: Record<string, any>;
  [key: string]: any;
}

/** Single request body */
export interface ValidateEventRequest {
  platform: string;
  payload: ActivityEventPayload;
}

/** Single success response */
export interface ValidateEventSuccess {
  status: 'pass';
  log_id: string;
}

/** Single failure response */
export interface ValidateEventFailure {
  status: 'fail';
  errors: string[];
}

export type ValidateEventResponse = ValidateEventSuccess | ValidateEventFailure;

/** Bulk response */
export interface ValidateEventsBulkResponse {
  status: 'success';
  processed: number;
  results: ValidateEventResponse[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const EVENTS_BASE_URL = 'https://events-ql.zebralearn.com';
const VALIDATE_EVENT_ENDPOINT = `${EVENTS_BASE_URL}/api/validate-event`;

// ─── Service Functions ────────────────────────────────────────────────────────

/**
 * Validate and log a single activity event.
 *
 * @example
 * await validateEvent({
 *   platform: 'app',
 *   payload: {
 *     event: 'screen_session',
 *     source: 'global',
 *     source_type: 'profile',
 *     meta: { platform: 'ios', os: '16.0', version: '1.0.0' },
 *   },
 * });
 */
export const validateEvent = async (
  request: ValidateEventRequest
): Promise<ValidateEventResponse> => {
  const { data } = await axios.post<ValidateEventResponse>(
    VALIDATE_EVENT_ENDPOINT,
    request,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return data;
};

/**
 * Validate and log multiple activity events in one request.
 *
 * @example
 * await validateEventsBulk([
 *   { platform: 'app', payload: { event: 'app_open', source: 'main' } },
 *   { platform: 'website', payload: { event: 'click', source: 'nav' } },
 * ]);
 */
export const validateEventsBulk = async (
  requests: ValidateEventRequest[]
): Promise<ValidateEventsBulkResponse> => {
  const { data } = await axios.post<ValidateEventsBulkResponse>(
    VALIDATE_EVENT_ENDPOINT,
    requests,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return data;
};

// ─── Existing Logging Helper ──────────────────────────────────────────────────

export const logPageView = async (eventData: any) => {
  try {
    addEventToQueue({
      ...eventData,
      created_at: moment().format('YYYY-MM-DDTHH:mm:ss.SSS'),
    });

    if (typeof window !== 'undefined' && window.posthog?.capture) {
      const { meta, ...restEventData } = eventData;

      window.posthog.capture(eventData.event, {
        ...restEventData,
        ...meta,
      });
    }
    if (eventData.event === 'signup') {
      await appwriteService.updateOnboardingPrefs(eventData);
    }
  } catch (error) {
    console.error('Error logging event to PostHog or backend:', error);
  }
};
